import { Point } from 'geojson';
import { sortBy } from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { SnTeam } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { SnSmartBadgeService } from '@lib/ui/badge';

import { SnLiveUser } from '../models';

export class SnUsersTracking {
  private readonly _userLoggedId$ = new Subject<SnLiveUser>();
  private readonly _userLoggedOut$ = new Subject<SnLiveUser>();
  private readonly _userCallSignChanged$ = new Subject<SnLiveUser>();
  private readonly _userLocationChanged$ = new Subject<SnLiveUser>();

  private readonly _userMapping = new Map<string, SnLiveUser>();
  private _userList: SnLiveUser[] = [];

  constructor(
    private readonly _usersAPI: SnUsersAPI,
    private readonly _smartBadge: SnSmartBadgeService,
  ) {}

  get userList(): SnLiveUser[] {
    return this._userList;
  }

  get userCount(): number {
    return this._userMapping.size;
  }

  get userLoggedIn$(): Observable<SnLiveUser> {
    return this._userLoggedId$.asObservable();
  }

  get userLoggedOut$(): Observable<SnLiveUser> {
    return this._userLoggedOut$.asObservable();
  }

  get userCallSignChanged$(): Observable<SnLiveUser> {
    return this._userCallSignChanged$.asObservable();
  }

  get userLocationChanged$(): Observable<SnLiveUser> {
    return this._userLocationChanged$.asObservable();
  }

  public addUser(user: SnLiveUser, emitEvent: boolean = true): void {
    this._userMapping.set(user.id, user);
    this._updateUserList();

    if (emitEvent) {
      this._userLoggedId$.next(user);
    }
  }

  public removeUser(id: string, emitEvent: boolean = true): void {
    this.getUser(id).subscribe((user) => {
      this._userMapping.delete(id);
      this._updateUserList();

      if (emitEvent) {
        this._userLoggedOut$.next(user);
      }
    });
  }

  public updateUserCallSign(
    id: string,
    callSign: string,
    team: SnTeam,
    emitEvent: boolean = true,
  ): void {
    this.getUser(id).subscribe((user) => {
      user.changeCallSign(callSign, team);

      if (emitEvent) {
        this._userCallSignChanged$.next(user);
      }
    });
  }

  public updateUserLocation(
    id: string,
    geometry: Point,
    altitude: number,
    speed: number,
    emitEvent: boolean = true,
  ): void {
    this.getUser(id).subscribe((user) => {
      if (!this._userList.find((u) => u.id === user.id)) {
        this.addUser(user);
      }
      user.move(geometry, speed, altitude);

      if (emitEvent) {
        this._userLocationChanged$.next(user);
      }
    });
  }

  public getUser(id: string): Observable<SnLiveUser> {
    return new Observable<SnLiveUser>((observer) => {
      const localUser = this._userMapping.get(id);

      if (localUser) {
        observer.next(localUser);
        observer.complete();
      } else {
        this._usersAPI
          .getRawUser(id)
          .pipe(
            map((response: any) => new SnLiveUser(response)),
            filter((user) => user.mobileLoggedIn),
          )
          .subscribe({
            next: (user) => {
              observer.next(user);
              observer.complete();
            },
          });
      }
    });
  }

  public sortUsersByPriority(): void {
    const sortHandler = (user: SnLiveUser) => [!user.isLive, user.callSign];

    this._userList = sortBy(this._userList, sortHandler);
  }

  public searchActiveUsers(query: string, limit: number = 10): Observable<SnLiveUser[]> {
    if (!query) {
      return of([]);
    }

    return new Observable((observer) => {
      const filteredUsers = this.userList
        .filter((user) => {
          const upperCaseQuery = query.toUpperCase();
          const fullName = user.fullName.toUpperCase();
          const callSign = user.callSign.toUpperCase();

          return fullName.includes(upperCaseQuery) || callSign.includes(upperCaseQuery);
        })
        .slice(0, limit);

      observer.next(filteredUsers);
      observer.complete();
    });
  }

  public clear(): void {
    this._userMapping.clear();
    this._updateUserList();
  }

  public destroy(): void {
    this.clear();

    this._userLoggedId$.complete();
    this._userLoggedOut$.complete();
    this._userCallSignChanged$.complete();
    this._userLocationChanged$.complete();
  }

  private _updateUserList(): void {
    this._userList = Array.from(this._userMapping.values());
    this._smartBadge.setBadge('live-tracking__user-count', this._userList.length);
  }
}
